import { uuToSim } from '../config';
import {
  validateGoalExplosionAudioDefinition,
  type GoalExplosionAudioDefinition,
} from '../audio/GoalExplosionAudioDefinition';
import { MEDIA_KEYS, mediaAssetUrl } from '../content/MediaUrls';
import {
  defaultGoalExplosionVfxDefinition,
  validateGoalExplosionVisual,
  type GoalExplosionVisual,
} from './GoalExplosionVisualDefinition';

export type GoalExplosionFalloff = 'linear' | 'smoothstep';
export type GoalExplosionDirection = 'away' | 'toward';
export type GoalExplosionPlacement = 'ball-entry' | 'goal-center';

export interface RadialVehicleVelocityEffect {
  readonly kind: 'radial-vehicle-velocity';
  readonly direction: GoalExplosionDirection;
  readonly radius: number;
  readonly maxDeltaSpeed: number;
  readonly verticalBias: number;
  readonly falloff: GoalExplosionFalloff;
}

export type GoalExplosionGameplayEffect = RadialVehicleVelocityEffect;

export interface DemolitionExplosionVisual {
  readonly fireParticles: number;
  readonly smokeParticles: number;
  readonly hotColor: number;
  readonly fireColor: number;
  readonly smokeColor: number;
  readonly duration: number;
}

export interface GoalEntryVisual {
  readonly enabled: boolean;
  readonly darkening: number;
  readonly hologramColor: number;
  readonly hologramIntensity: number;
  readonly hologramDistortion: number;
  readonly hologramPulseAmount: number;
  readonly hologramSpeed: number;
}

export interface FlipResetVisual {
  readonly enabled: boolean;
  readonly duration: number;
  readonly color: number;
  readonly opacity: number;
  readonly shellScale: number;
  readonly cupAngleRadians: number;
  readonly rimOpacity: number;
}

export interface GoalExplosionDefinition {
  readonly placement: GoalExplosionPlacement;
  readonly visual: GoalExplosionVisual;
  readonly audio: GoalExplosionAudioDefinition;
  readonly gameplay: readonly GoalExplosionGameplayEffect[];
}

export interface MatchEffectsDefinition {
  readonly demolitionExplosion: Readonly<{
    visual: DemolitionExplosionVisual;
  }>;
  readonly goalExplosion: GoalExplosionDefinition | null;
  readonly goalEntry: GoalEntryVisual;
  readonly flipReset: Readonly<{
    visual: FlipResetVisual;
  }>;
}

const DEFAULT_MATCH_EFFECTS: MatchEffectsDefinition & { readonly goalExplosion: GoalExplosionDefinition } = Object.freeze({
  demolitionExplosion: Object.freeze({
    visual: Object.freeze({
      fireParticles: 64,
      smokeParticles: 28,
      hotColor: 0xffcf55,
      fireColor: 0xff5d16,
      smokeColor: 0x565a62,
      duration: 0.72,
    }),
  }),
  goalExplosion: Object.freeze({
    placement: 'goal-center' as const,
    visual: Object.freeze({
      duration: 3.5,
      offset: Object.freeze({
        vertical: uuToSim(100),
        towardField: uuToSim(750),
      }),
      model: Object.freeze({
        url: mediaAssetUrl(MEDIA_KEYS.goalMagicPortalModel),
        sceneIndex: 0,
        scale: 0.135,
        sourceAnchor: Object.freeze({
          x: 5.258419036865234,
          y: 0,
          z: -5.1285052645043265,
        }),
        rotationYRadians: 0,
        enterTransition: Object.freeze({ kind: 'scale' as const, duration: 0.34, distance: 0 }),
        exitTransition: null,
        animations: Object.freeze([
          Object.freeze({
            id: 'all-clips',
            clip: Object.freeze({ kind: 'all' as const }),
            clipStartSeconds: 0,
            clipEndSeconds: null,
            startSeconds: 0,
            endSeconds: null,
            mode: 'loop' as const,
            timeScale: 0.9,
            weight: 1,
          }),
        ]),
      }),
      vfx: Object.freeze(defaultGoalExplosionVfxDefinition()),
      coreColor: 0xdaf8ff,
    }),
    audio: Object.freeze({
      routing: 'global' as const,
      impact: Object.freeze({
        url: mediaAssetUrl(MEDIA_KEYS.goalMagicPortalImpact),
        gain: 0.5,
      }),
      ambience: null,
      animationCues: Object.freeze([]),
    }),
    gameplay: Object.freeze([
      Object.freeze({
        kind: 'radial-vehicle-velocity' as const,
        direction: 'away' as const,
        radius: uuToSim(5000),
        maxDeltaSpeed: uuToSim(5200),
        verticalBias: 0.28,
        falloff: 'linear' as const,
      }),
    ]),
  }),
  goalEntry: Object.freeze({
    enabled: true,
    darkening: 0.96,
    hologramColor: 0xcdf7ff,
    hologramIntensity: 0.44,
    hologramDistortion: 0.58,
    hologramPulseAmount: 0.018,
    hologramSpeed: 2.4,
  }),
  flipReset: Object.freeze({
    visual: Object.freeze({
      enabled: true,
      duration: 0.24,
      color: 0x9ce8ff,
      opacity: 0.48,
      shellScale: 1.025,
      cupAngleRadians: 0.62,
      rimOpacity: 0.32,
    }),
  }),
});

export function defaultMatchEffectsDefinition(): MatchEffectsDefinition & { readonly goalExplosion: GoalExplosionDefinition } {
  return DEFAULT_MATCH_EFFECTS;
}

export function validateMatchEffectsDefinition(definition: MatchEffectsDefinition): void {
  const demolition = definition.demolitionExplosion.visual;
  positiveInteger(demolition.fireParticles, 'demolition fire particle count');
  positiveInteger(demolition.smokeParticles, 'demolition smoke particle count');
  positive(demolition.duration, 'demolition duration');
  color(demolition.hotColor, 'demolition hot color');
  color(demolition.fireColor, 'demolition fire color');
  color(demolition.smokeColor, 'demolition smoke color');

  if (definition.goalExplosion !== null) validateGoalExplosionDefinition(definition.goalExplosion);

  if (typeof definition.goalEntry.enabled !== 'boolean') throw new Error('Goal-entry enabled must be a boolean.');
  unit(definition.goalEntry.darkening, 'goal-entry darkening');
  color(definition.goalEntry.hologramColor, 'goal-entry hologram color');
  unit(definition.goalEntry.hologramIntensity, 'goal-entry hologram intensity');
  unit(definition.goalEntry.hologramDistortion, 'goal-entry hologram distortion');
  unit(definition.goalEntry.hologramPulseAmount, 'goal-entry hologram pulse amount');
  positive(definition.goalEntry.hologramSpeed, 'goal-entry hologram speed');

  const flipReset = definition.flipReset.visual;
  if (typeof flipReset.enabled !== 'boolean') throw new Error('Flip-reset enabled must be a boolean.');
  positive(flipReset.duration, 'flip-reset duration');
  color(flipReset.color, 'flip-reset color');
  unit(flipReset.opacity, 'flip-reset opacity');
  if (!Number.isFinite(flipReset.shellScale) || flipReset.shellScale < 1) {
    throw new Error('Flip-reset shell scale must be a finite number greater than or equal to 1.');
  }
  if (!Number.isFinite(flipReset.cupAngleRadians)
    || flipReset.cupAngleRadians <= 0
    || flipReset.cupAngleRadians >= Math.PI / 2) {
    throw new Error('Flip-reset cup angle must be between 0 and PI/2 radians.');
  }
  unit(flipReset.rimOpacity, 'flip-reset rim opacity');
}

export function validateGoalExplosionDefinition(explosion: GoalExplosionDefinition): void {
  const goal = explosion.visual;
  if (explosion.placement !== 'ball-entry' && explosion.placement !== 'goal-center') {
    throw new Error('Goal explosion placement must be ball-entry or goal-center.');
  }
  validateGoalExplosionVisual(goal);
  validateGoalExplosionAudioDefinition(explosion.audio);
  const animationLayers = new Map(goal.model?.animations.map(layer => [layer.id, layer]) ?? []);
  for (const cue of explosion.audio.animationCues) {
    const layer = animationLayers.get(cue.animationLayerId);
    if (!layer) {
      throw new Error(`Goal explosion animation audio cue ${cue.animationLayerId} references a missing animation layer.`);
    }
    const expectedStartSeconds = layer.startSeconds + cue.delaySeconds;
    if (Math.abs(cue.startSeconds - expectedStartSeconds) > 1e-6) {
      throw new Error(`Goal explosion animation audio cue ${cue.animationLayerId} start must match its animation layer plus delay.`);
    }
  }

  if (explosion.gameplay.length === 0) {
    throw new Error('Goal explosion must declare at least one gameplay effect.');
  }
  for (const effect of explosion.gameplay) validateGameplayEffect(effect);
}

function validateGameplayEffect(effect: GoalExplosionGameplayEffect): void {
  switch (effect.kind) {
    case 'radial-vehicle-velocity':
      if (effect.direction !== 'away' && effect.direction !== 'toward') {
        throw new Error('Goal explosion radial direction must be away or toward.');
      }
      if (effect.falloff !== 'linear' && effect.falloff !== 'smoothstep') {
        throw new Error('Goal explosion radial falloff must be linear or smoothstep.');
      }
      positive(effect.radius, 'goal explosion radial radius');
      positive(effect.maxDeltaSpeed, 'goal explosion radial max delta speed');
      if (!Number.isFinite(effect.verticalBias) || effect.verticalBias < 0) {
        throw new Error('Goal explosion vertical bias must be a non-negative finite number.');
      }
      return;
  }
}

function positiveInteger(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error(`${label} must be a positive integer.`);
}

function positive(value: number, label: string): void {
  if (!Number.isFinite(value) || value <= 0) throw new Error(`${label} must be a positive finite number.`);
}

function unit(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error(`${label} must be between 0 and 1.`);
}

function color(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0 || value > 0xffffff) {
    throw new Error(`${label} must be an RGB integer between 0x000000 and 0xFFFFFF.`);
  }
}
